import * as DM from "@radix-ui/react-dropdown-menu"
import { faCheck, faHourglass } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useState } from "react"
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  useFetcher,
  useLoaderData,
  useSearchParams,
  Form,
  type FormMethod,
} from "react-router-dom"

import { fetchAPI } from "../utils"
import { Header, MessageItem } from "../components"
import type { Queue } from "../components/QueueDetail"

async function action({ request }: ActionFunctionArgs) {
  const url = new URL(request.url)
  const { action, messageIds, priority } = Object.fromEntries(await request.formData()) as Record<string, string>
  const query = url.searchParams.get("query") ?? ""
  const queueName = url.searchParams.get("queueName") ?? ""
  const urlPrefix = url.pathname.endsWith("deadLetters") ? "deadLetters/" : ""
  const splitedMessageIds = messageIds.split(",")
  let endpoint = ""
  let body = {}
  let method = ""

  switch (action) {
    case "delete":
      endpoint = `/api/queues/${urlPrefix}delete/${queueName}`
      body = { messageIds: splitedMessageIds, query }
      method = "POST"
      break
    case "retry":
      endpoint = `/api/queues/retry/${queueName}/${priority ?? "0"}`
      body = {
        messageIds: splitedMessageIds,
        priority,
        query,
      }
      method = "PUT"
      break
    case "fail":
      endpoint = `/api/queues/fail/${queueName}`
      body = {
        messageIds: splitedMessageIds,
        query,
      }
      method = "POST"
      break
    default:
      break
  }

  return await fetchAPI(endpoint, { body, method }, "raw")
}

async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const { page = 1, pageSize = 10, ...query } = Object.fromEntries(new URL(request.url).searchParams)

  const urlPrefix = url.pathname.endsWith("deadLetters") ? "deadLetters/" : ""
  const queueName = url.searchParams.get("queueName") ?? ""

  const [queue, data] = await Promise.all([
    fetchAPI<Queue>(`/api/queues/${queueName}`),
    fetchAPI(
      `/api/queues/${urlPrefix}items/${queueName}/${page}/${pageSize}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...query }),
      },
      "raw",
    ),
  ])

  const queueMessage = (await data.result?.json()) as {
    items: Message[]
    totalCount: number
  }

  return {
    queue: queue.result,
    data: queueMessage,
    totalPages: Math.ceil(queueMessage?.totalCount / +pageSize),
    urlPrefix,
  }
}

const Route = ({ deadLetters = false }: RouteProps) => {
  const fetcher = useFetcher<LoaderResponse>()
  const { queue, data, totalPages } = useLoaderData() as LoaderResponse
  const [searchParams, setSearchParams] = useSearchParams()
  const [selected, setSelected] = useState<"all" | string[]>([])
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [selectAllOpen, setSelectAllOpen] = useState(false)
  const page = +(searchParams.get("page") ?? 1)
  const pageSize = +(searchParams.get("pageSize") ?? 10)
  const priority = searchParams.get("priority")
  const selectedAllNumbers = selected === "all" ? (data?.totalCount ?? 0) : selected.length
  const selectedPageNumbers = selected === "all" ? (data?.totalCount ?? 0) : data?.items?.length

  return (
    <div className="flex w-full flex-col items-start justify-start gap-5 bg-base-300 px-5 py-5 h-screen">
      <div className="w-full space-y-5">
        <Header />
        <div className="grid w-full grid-cols-[minmax(0,20%),minmax(65%,1fr)] grid-rows-3 gap-3">
          <div className="text-black col-start-1 row-span-full flex min-h-0 min-w-0 items-center justify-start gap-2">
            <span className="text-sm font-bold" aria-label="Dead letters or messages">
              {searchParams.get("queueName")} - {deadLetters ? "Dead Letters" : "Messages"}
            </span>
            <FontAwesomeIcon icon={fetcher.state !== "idle" ? faHourglass : faCheck} />
          </div>
          <Form
            className="col-start-2 row-start-1 flex min-h-0 min-w-0 items-center justify-end gap-2"
            onSubmit={() => setSelected([])}
          >
            <input type="hidden" name="page" value={page} />
            <input type="hidden" name="pageSize" value={pageSize} />
            <input type="hidden" name="queueName" value={searchParams.get("queueName") ?? ""} />
            <input
              type="text"
              name="query"
              id="regex-input"
              placeholder="Enter search term (regex)"
              className="input-bordered input-accent input input-sm basis-1/2 bg-base-100 py-1 px-2 text-sm text-base-content focus:outline-none"
              defaultValue={searchParams.get("query") ?? ""}
            />
            <button type="submit" className="text-black btn-outline btn-accent btn-sm btn text-sm">
              Go!
            </button>
          </Form>
          <div className="col-start-2 row-start-2 flex min-h-0 min-w-0 flex-wrap items-center justify-end gap-4">
            <div className="text-black flex items-center justify-start gap-2 text-sm">
              <span className="font-bold">Selected:</span>
              <span className="min-w-max">
                {selectedAllNumbers} of {selectedPageNumbers}
              </span>
            </div>
            <div className="text-black flex items-center justify-start gap-2 text-sm">
              <span className="font-bold">Select:</span>
              <DM.Root open={selectAllOpen} onOpenChange={setSelectAllOpen}>
                <DM.Trigger
                  type="button"
                  onClick={() => {
                    fetcher.submit(
                      {
                        page: "1",
                        pageSize: String(data.totalCount),
                      },
                      { method: "post" },
                    )
                  }}
                  className="text-black btn-outline btn-accent btn-sm btn"
                >
                  All
                </DM.Trigger>
                <DM.Portal>
                  <DM.Content className="flex flex-col items-center justify-end gap-3 rounded bg-base-100 p-3 text-base-content">
                    <span className="text-sm">
                      If new messages available. Page will be refreshed and all new messages will be selected.
                    </span>
                    <button
                      type="button"
                      className="btn-outline btn-sm btn bg-base-200"
                      onClick={() => {
                        setSelected("all")
                        setSelectAllOpen(false)
                      }}
                    >
                      OK
                    </button>
                  </DM.Content>
                </DM.Portal>
              </DM.Root>
              <button
                type="button"
                onClick={() => setSelected(data?.items?.map((i) => i.id) ?? [])}
                className="text-black btn-outline btn-accent btn-sm btn"
                aria-label="Select all messages for this page"
              >
                Page
              </button>
              <button
                type="button"
                onClick={() => setSelected([])}
                className="text-black btn-outline btn-accent btn-sm btn"
                aria-label="Clear selection"
              >
                None
              </button>
            </div>
            <div className="text-black flex items-center justify-start gap-2 text-sm">
              <label htmlFor="adjust-pty" className="font-bold">
                Adjust Pty:
              </label>
              <select
                name="priority"
                id="adjust-pty"
                defaultValue={priority ?? ""}
                onChange={(e) => {
                  searchParams.set("priority", e.target.value)
                  setSearchParams(searchParams)
                }}
                className="select-bordered select-accent select-sm select max-w-max bg-base-100 text-base-content focus:outline-none"
              >
                <option value="">Leave as is</option>
                {Array.from({ length: queue.arguments.maxPriority ?? 0 }).map((_, priority) => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-evenly gap-2">
              {deadLetters ? (
                <Fetcher
                  selected={selected}
                  method="put"
                  action="retry"
                  disabled={selected.length === 0}
                  resetSelected={() => setSelected([])}
                />
              ) : (
                <Fetcher
                  selected={selected}
                  method="post"
                  action="fail"
                  disabled={selected.length === 0}
                  resetSelected={() => setSelected([])}
                />
              )}
              <DM.Root open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DM.Trigger
                  type="button"
                  disabled={selected.length === 0}
                  className="btn-outline btn-accent btn-sm btn text-sm text-accent disabled:bg-base-100 disabled:text-base-content"
                >
                  Delete
                </DM.Trigger>
                <DM.Portal>
                  <DM.Content className="space-y-3 rounded bg-base-100 p-3">
                    <span className="text-sm text-base-content">
                      Are you sure you want to delete the selected message?
                    </span>
                    <fetcher.Form
                      method="post"
                      className="flex w-full items-center justify-end gap-3"
                      onSubmit={() => {
                        setSelected([])
                        setDeleteOpen(false)
                      }}
                    >
                      <input type="hidden" name="action" value="delete" />
                      <input type="hidden" name="messageIds" value={selected} />
                      <button type="submit" className="btn-sm btn bg-error text-error-content">
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelected([])
                          setDeleteOpen(false)
                        }}
                        className="btn-sm btn bg-base-200 text-base-content"
                      >
                        Cancel
                      </button>
                    </fetcher.Form>
                  </DM.Content>
                </DM.Portal>
              </DM.Root>
            </div>
          </div>
          <div className="col-start-2 row-start-3 flex min-w-0 flex-wrap items-center justify-end gap-4">
            <div className="flex items-center justify-start gap-2 text-sm">
              <span className="text-black">
                {(() => {
                  const lastItemIndex = page * pageSize
                  const resultsCount = data?.totalCount ?? 0
                  const firstItemIndex = lastItemIndex - pageSize + 1

                  return `Showing ${Math.min(firstItemIndex, resultsCount)} to ${Math.min(
                    lastItemIndex,
                    resultsCount,
                  )} of ${resultsCount} result${resultsCount !== 1 ? "s" : ""}`
                })()}
              </span>
              <div className="flex items-center justify-start gap-3">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() =>
                    page > 1 &&
                    setSearchParams((params) => {
                      params.set("page", String(page - 1))
                      return params
                    })
                  }
                  className="btn-outline btn-accent btn-sm btn text-sm text-accent disabled:bg-base-100 disabled:text-base-content"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page === (totalPages ?? 1)}
                  onClick={() =>
                    page < totalPages &&
                    setSearchParams((params) => {
                      params.set("page", String(+page + 1))
                      return params
                    })
                  }
                  className="btn-outline btn-accent btn-sm btn text-sm text-accent disabled:bg-base-100 disabled:text-base-content"
                >
                  Next
                </button>
              </div>
            </div>
            <div className="text-black flex items-center justify-start gap-2 text-sm">
              <label htmlFor="page-size" className="font-bold">
                Page Size:
              </label>
              <select
                name="pageSize"
                id="page-size"
                value={pageSize}
                disabled={fetcher.state !== "idle"}
                onChange={(e) =>
                  setSearchParams((params) => {
                    params.set("page", "1")
                    params.set("pageSize", e.target.value)
                    return params
                  })
                }
                className="select-bordered select-accent select select-sm max-w-max bg-base-100 text-accent focus:outline-none disabled:text-base-content disabled:select-bordered"
              >
                {[5, 10, 20, 30, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      <div className="divider" />
      <div
        className="
      flex w-full flex-col gap-2 rounded text-base-content
      overflow-y-auto h-[42.5rem]"
      >
        {data?.items?.map((message) => (
          <MessageItem
            key={message.id}
            message={message}
            selected={selected === "all" || selected.includes(message.id)}
            toggleSelected={() => {
              if (selected === "all") {
                setSelected(data?.items?.map((item) => item.id).filter((item) => item !== message.id) ?? [])
                return
              }

              setSelected(
                selected.includes(message.id) ? selected.filter((s) => s !== message.id) : [...selected, message.id],
              )
            }}
          />
        ))}
      </div>
    </div>
  )
}

Route.action = action
Route.loader = loader

export default Route

const Fetcher = ({ selected, method, action, label, disabled, resetSelected }: FetcherProps) => {
  const fetcher = useFetcher<LoaderResponse>()
  return (
    <fetcher.Form method={method} className="flex shrink-0 items-center justify-end" onSubmit={resetSelected}>
      <input type="hidden" name="action" value={action} />
      <input type="hidden" name="messageIds" value={selected} />
      <button
        type="submit"
        disabled={disabled}
        className="btn-outline btn-accent btn-sm btn whitespace-nowrap text-sm text-accent disabled:bg-base-100 disabled:text-base-content"
      >
        {label ?? action}
      </button>
    </fetcher.Form>
  )
}

interface FetcherProps {
  selected: string[] | string
  method: FormMethod
  action: string
  label?: string
  disabled?: boolean
  resetSelected: () => void
}

interface RouteProps {
  deadLetters?: boolean
}

interface Data {
  items?: Message[]
  totalCount?: number
}

interface LoaderResponse {
  queue: Queue
  queueName: string
  data: Data
  totalPages: number
}

export interface Message {
  id: string
  payload: Payload
  error: string
  trace: string
  firstDeath: string
  lastDeath: string
  priority?: number
}

export interface Payload {
  rsDocsWebAddress: string
  rsDocsApiAddress: string
  clientName: string
  brand: string
  caseReference: string
  fileReference: string
  [x: string]: any
}
